import { graphql, useStaticQuery } from 'gatsby';
import { find, map, propEq } from 'ramda';
import React, { FC, useContext } from 'react';
import styled from 'styled-components';

import BasicText from '@ui/atoms/BasicText';
import HeadingText from '@ui/atoms/HeadingText';
import StyledLink from '@ui/atoms/StyledLink';
import WYSIWYGContent from '@ui/atoms/WYSIWYGContent';

import SpacedGridItem from '@ui/molecules/SpacedGridItem';

import FlowContext from '@providers/FlowContextProvider';

import { IFlowBailoutStepProps } from '@folx-types/flow-steps';

import theme from '@theme/FolxTheme/FolxTheme';

interface IAgeIneligibilityStepProps extends IFlowBailoutStepProps {}

interface IAgeIneligibilityStepContent {
	content: {
		title: string;
		subtitle: string;
		resources: IProductResourceContent[];
		navigation_link: string;
	};
}

interface IWebsiteResourceContent {
	title: string;
	url: string;
	blurb: string;
}
interface IProductResourceContent {
	product_key: string;
	resource_content: string;
	subtitle: string;
	hotline_resources: string;
	website_resources: IWebsiteResourceContent[];
}

const ContentWYSIWYG = styled(WYSIWYGContent)`
	p > span a,
	p > strong span a {
		display: inline-block;
	}

	p > strong span:last-of-type {
		display: inline-block;
		margin-top: ${theme.spacing.lg};
	}
`;

const ContentTitle = styled(StyledLink)`
	color: ${theme.tints.darken40(theme.colors.cyan)};
	display: inline-block;
	margin-top: ${theme.spacing.lg};
	margin-bottom: ${theme.spacing.xs};
`;

const StyledHr = styled.hr`
	margin-top: ${theme.spacing.lgxl};
	margin-bottom: ${theme.spacing.sm};
`;

const NavLink = styled(WYSIWYGContent)`
	padding-top: ${theme.spacing.xxl};
	padding-bottom: ${theme.spacing.xxl};
	font-weight: bold;
	text-align: center;
`;

const WebsiteResource = styled(SpacedGridItem)`
	p > span a {
		margin-top: 0;
	}
`;

const AgeIneligibilityStep: FC<IAgeIneligibilityStepProps> = () => {
	const context = useContext(FlowContext);
	const { destinationFlow, flow } = context;
	const { content } = useStaticQuery<IAgeIneligibilityStepContent>(
		graphql`
			{
				content: butterPage(slug: { eq: "age-ineligibility" }) {
					title
					subtitle
					resources {
						product_key
						resource_content
						subtitle
						hotline_resources
						website_resources {
							title
							url
							blurb
						}
					}
					navigation_link
				}
			}
		`
	);

	const { title, subtitle, resources, navigation_link } = content;

	const resourceContent =
		find<IProductResourceContent>(propEq('product_key', destinationFlow))(resources) ??
		find<IProductResourceContent>(propEq('product_key', flow))(resources);
	const { website_resources } = resourceContent ?? [];
	const renderWebsiteResources = ({ title, url, blurb }: IWebsiteResourceContent) => {
		return (
			<WebsiteResource key={title}>
				<BasicText>
					<ContentTitle to={url} variant="underline" color="cyan" tint="darken40">
						{title}
					</ContentTitle>
				</BasicText>
				<ContentWYSIWYG
					dangerouslySetInnerHTML={{
						__html: blurb,
					}}
				/>
			</WebsiteResource>
		);
	};

	return (
		<SpacedGridItem>
			<SpacedGridItem marginBottom="sm">
				<HeadingText>{title}</HeadingText>
			</SpacedGridItem>
			<ContentWYSIWYG
				dangerouslySetInnerHTML={{
					__html: subtitle,
				}}
			/>
			<ContentWYSIWYG
				dangerouslySetInnerHTML={{
					__html: resourceContent?.hotline_resources,
				}}
			/>
			<StyledHr />
			{map(renderWebsiteResources, website_resources ?? [])}
			<NavLink
				dangerouslySetInnerHTML={{
					__html: `${navigation_link}`,
				}}
			/>
		</SpacedGridItem>
	);
};

export default AgeIneligibilityStep;
